/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br."Knowledge for Decisions (K4D)" and the slogan"Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from'react';
import { cn } from'@/shared/ui/utils/tailwind';
import Card from'./card/Card';

export type PaperProps = {
  children: React.ReactNode;
  padding?:'xs' |'sm' |'md' |'lg' |'xl' | number;
  radius?:'xs' |'sm' |'md' |'lg' |'xl';
  withBorder?: boolean;
  shadow?:'xs' |'sm' |'md' |'lg' |'xl';
  className?: string;
  style?: React.CSSProperties;
};

const paddingMap = {
  xs:'p-2',
  sm:'p-3',
  md:'p-4',
  lg:'p-6',
  xl:'p-8',
};

const radiusMap = {
  xs:'rounded-(--radius-sm)',
  sm:'rounded-(--radius-sm)',
  md:'rounded-(--radius-md)',
  lg:'rounded-(--radius-lg)',
  xl:'rounded-(--radius-xl)',
};

const shadowMap = {
  xs:'shadow-sm',
  sm:'shadow',
  md:'shadow-md',
  lg:'shadow-lg',
  xl:'shadow-xl',
};

/**
 * Paper component with Tailwind styling.
 * Replaces Mantine Paper - essentially a styled div with padding, radius, and shadow.
 *
 * @deprecated Prefer using`<div className="bg-[var(--surface-primary)] p-4 rounded-md">` directly.
 * @param {PaperProps} props - Component properties.
 * @returns {JSX.Element} Rendered paper container.
 */
export const Paper: React.FC<PaperProps> = ({
  children,
  padding ='md',
  radius ='md',
  withBorder = false,
  shadow,
  className,
  style,
}) => {
  const paddingClass = typeof padding ==='number' ? undefined : paddingMap[padding];
  const paddingStyle = typeof padding ==='number' ? { padding:`${padding}px` } : undefined;
  const radiusClass = radiusMap[radius];
  const shadowClass = shadow ? shadowMap[shadow] :'';

  return (
    <div
      className={cn(
        'bg-[var(--surface-primary)]',
        paddingClass,
        radiusClass,
        withBorder &&'border border-[var(--border-medium)]',
        shadowClass,
        className
      )}
      style={{ ...paddingStyle, ...style }}
    >
      {children}
    </div>
  );
};

export default Paper;
